import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import './i18n/config'; // bootstraps i18next before any component renders
import { heIL, enUS } from '@mui/x-data-grid/locales';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { prefixer } from 'stylis';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { DataProvider } from './context/DataContext';
import { ImpersonationProvider } from './context/ImpersonationContext';
import { getBootDirection, getBootLocale } from './i18n/config';

// ---------------------------------------------------------------------------
// Bilingual boot
// ---------------------------------------------------------------------------
// Direction + Emotion cache + MUI locale are decided ONCE at boot, based on
// the URL (?lang=en on public /campaign/* routes flips us to LTR English).
// Everywhere else — admin, dashboard, builder chrome — we stay RTL Hebrew,
// IDENTICAL to the previous hard-coded behavior. Legacy campaigns without
// ?lang= therefore render byte-for-byte the same as before this change.
// See frontend/src/i18n/config.ts for the boot-locale rules.
// ---------------------------------------------------------------------------
const bootLocale = getBootLocale();
const bootDirection = getBootDirection();

const theme = createTheme(
  { direction: bootDirection },
  bootDirection === 'ltr' ? enUS : heIL
);

// Emotion cache. We keep the SAME key/plugins as before for the RTL case
// (only `prefixer`, no stylis-plugin-rtl — the existing app relied on
// MUI's own theme.direction for RTL flipping, not on stylis). For LTR we
// use a different cache key so emotion treats the trees as distinct.
const emotionCache = createCache({
  key: bootDirection === 'ltr' ? 'mui' : 'muirtl',
  stylisPlugins: [prefixer],
});

// Apply direction at the document level so global CSS (Tailwind utilities,
// custom CSS in index.css) sees the correct value. This only takes effect
// on first paint, never changes mid-session.
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('dir', bootDirection);
  document.documentElement.setAttribute('lang', bootLocale);
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <ImpersonationProvider>
          <DataProvider>
            <App />
          </DataProvider>
        </ImpersonationProvider>
      </ThemeProvider>
    </CacheProvider>
  </React.StrictMode>,
)
